import React from 'react';
import { Box, Button, CloseButton, Drawer, Group, Title, useMantineTheme } from '@mantine/core';
import type { DrawerProps } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft } from '@tabler/icons-react';

type StudioDrawerProps = {
    /** Whether the drawer is open. */
    opened: boolean;
    /** Dismiss the drawer (X, overlay click, Escape). */
    onClose: () => void;
    /** Heading shown at the top of the drawer. */
    title: React.ReactNode;
    /**
     * Renders a "Back" affordance instead of the close X — for nested drawers
     * (e.g. New component opened from the New scene component dropdown), where
     * dismissing should return to the parent drawer rather than close outright.
     */
    onBack?: () => void;
    /**
     * Pinned action bar along the bottom (typically Cancel / Save). Stays put
     * while the body scrolls; omit for content that has no primary action
     * (e.g. an "Add scene" picker grid).
     */
    footer?: React.ReactNode;
    /**
     * Desktop width — any Mantine `Drawer` size. The drawer is always full-screen
     * below the `sm` breakpoint regardless of this value.
     */
    size?: DrawerProps['size'];
    /** Called once the close animation has finished, for deferred cleanup. */
    onExitTransitionEnd?: () => void;
    /** Overrides the default stacking order — e.g. to sit above an already-open modal. */
    zIndex?: DrawerProps['zIndex'];
    children: React.ReactNode;
};

// Cancels the body's padding so the sticky header/footer span its full width and
// sit flush against its edges.
const bleed = {
    marginInline: 'calc(-1 * var(--mantine-spacing-md))',
    background: 'var(--mantine-color-body)',
} as const;

/**
 * The Studio's shared editor surface: a right-docked drawer on desktop that
 * becomes a full-screen sheet on mobile. Every scene/component editor mounts
 * inside one so the responsive behaviour, header and pinned footer are defined
 * once. Uses the plain `Drawer` (like {@see RouterDrawer}) with a sticky header
 * and footer so the body scrolls between them.
 */
const StudioDrawer: React.FC<StudioDrawerProps> = ({
    opened,
    onClose,
    title,
    onBack,
    footer,
    size = 'lg',
    onExitTransitionEnd,
    zIndex,
    children,
}) => {
    const theme = useMantineTheme();
    // `undefined` on the first render (pre-hydration) reads as not-mobile,
    // matching the desktop-first default; it resolves on mount.
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            onExitTransitionEnd={onExitTransitionEnd}
            zIndex={zIndex}
            position="right"
            size={isMobile ? '100%' : size}
            padding="md"
            withCloseButton={false}
            overlayProps={{ backgroundOpacity: 0.55, blur: 2 }}
            // Full-height flex column so the footer is pinned to the drawer's
            // bottom regardless of how much content there is.
            styles={{ body: { minHeight: '100%', display: 'flex', flexDirection: 'column' } }}
        >
            <Box
                component="header"
                style={{
                    ...bleed,
                    flex: '0 0 auto',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    marginTop: 'calc(-1 * var(--mantine-spacing-md))',
                    padding: 'var(--mantine-spacing-md)',
                }}
            >
                {onBack && (
                    <Button
                        variant="subtle"
                        color="gray"
                        size="compact-sm"
                        px={4}
                        mb={6}
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={onBack}
                    >
                        Back
                    </Button>
                )}
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
                    <Title order={3} style={{ minWidth: 0 }}>
                        {title}
                    </Title>
                    {!onBack && (
                        <CloseButton size="lg" aria-label="Close drawer" onClick={onClose} />
                    )}
                </Group>
            </Box>

            {/* Grows to fill, so the footer is pushed to the bottom when content
                is short and scrolls beneath the sticky footer when it's long. */}
            <Box style={{ flex: '1 0 auto', minHeight: 0 }}>{children}</Box>

            {footer && (
                <Box
                    component="footer"
                    style={{
                        ...bleed,
                        flex: '0 0 auto',
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 2,
                        marginBottom: 'calc(-1 * var(--mantine-spacing-md))',
                        marginTop: 'var(--mantine-spacing-md)',
                        padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-md)',
                        borderTop: '1px solid var(--mantine-color-default-border)',
                    }}
                >
                    {footer}
                </Box>
            )}
        </Drawer>
    );
};

export default StudioDrawer;
